import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity, Search, Banknote, Beaker, Bell, BriefcaseMedical, Building2, CalendarCheck, CalendarClock, CalendarPlus,
  ChevronLeft, ChevronRight,
  ClipboardCheck, Eye, FileBadge, FilePenLine, FileText,
  FlaskConical, Gauge,
  History, Hospital, IdCard, Inbox, KeyRound, LayoutGrid, ListChecks, LogOut, Mail, Menu,
  PenLine, ShieldCheck, Siren, SlidersHorizontal, Stethoscope, Tent,
  Upload, UserPlus, Users,
  UsersRound, Wallet, X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Principal } from '@dru/types';
import { InfoTip, TAP_AREA, ThemeToggle, cn } from '@dru/ui';
import { useAuth } from '../lib/auth';
import { BreadcrumbProvider, BreadcrumbContext, type BreadcrumbItem } from './BreadcrumbContext';
import { NotificationCenter } from '../components/NotificationCenter';
import { PlatformSearch } from '../components/PlatformSearch';
import { EmployeeSearch } from '../components/EmployeeSearch';
import { PlantSwitcher, storedViewPlant, storeViewPlant } from '../components/PlantSwitcher';
import { api } from '../lib/api';
import { BRAND } from '@dru/types';

/**
 * The console shell.
 *
 * Navigation is permission-filtered — a link to a screen that will 403 is
 * worse than no link, because it teaches people that errors are normal. The
 * filtering is usability, not security: the same permission is enforced on
 * every endpoint behind these screens.
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Any one of these is enough to show the link. */
  need?: string | string[];
  /**
   * Pages under this one, shown as a small tree beneath it while the reader
   * is inside it (the 29 Oct 2026 layout). Each is permission-filtered like
   * a top-level item; fewer than two left and the tree is not drawn.
   */
  children?: Array<{ to: string; label: string; need?: string | string[]; end?: boolean }>;
}

/**
 * Navigation is grouped, because a flat list of nine is a list nobody reads.
 *
 * The section label is the cheapest possible orientation: it tells somebody
 * which part of their job they are in before they have read a single item.
 */
interface NavSection {
  label?: string;
  items: NavItem[];
}

/**
 * One console per place of work, not one console with everything in it.
 *
 * A hospital coordinator, a bench technician and a certifying surgeon do
 * different jobs in different rooms. Showing all four navigations to everyone
 * and relying on permission filtering to empty three of them would leave the
 * reader deciding which section is theirs — and the sections are not
 * interchangeable: entering a result and releasing one are, by design, never
 * the same person.
 */
export interface Console {
  key: string;
  prefix: string;
  label: string;
  /** Shown when this console is offered from inside another one. */
  icon: LucideIcon;
  /** Any one of these is enough to be sent here at sign-in. */
  need: string[];
  /**
   * A second gate, for the console that is decided by WHO you are rather than
   * by what you may do.
   *
   * The venue console is the only one that needs it. A hospital account and
   * the older company-bound coordinator hold overlapping permissions — both
   * check people in — so permissions alone cannot tell them apart. What can
   * is that a venue account belongs to a venue and to no company.
   */
  only?: (principal: Principal | null | undefined) => boolean;
  /**
   * Among those who MAY use this console, who it is home for at sign-in.
   *
   * Distinct from `only`, and the distinction is the point. Operations and
   * the platform administrator both hold the CRM, but only one of them starts
   * their day in it — the administrator has a wider console of their own.
   * Gating with `only` would have taken the lead inbox away from the
   * administrator entirely; gating the landing leaves it one click away in
   * the console switcher, which is what "reachable but not home" means.
   */
  home?: (principal: Principal | null | undefined) => boolean;
  /**
   * Where this console opens, when that is not its first item.
   *
   * The platform console lists Overview first because that is where an
   * administrator starts. Finance holds Overview too, but its work is the
   * ledger; returning null keeps the first-item rule for everyone else.
   */
  start?: (permissions: ReadonlySet<string>) => string | null;
  sections: NavSection[];
}

/** A hospital or laboratory account: one venue, no company. */
const isVenueAccount = (p: Principal | null | undefined) => Boolean(p?.partner);
/** What the venue's Staff screen calls its roles; the header says the same. */
const VENUE_ROLE_NAME: Record<string, string> = {
  PARTNER_ADMIN: 'Venue admin',
  HOSPITAL_COORDINATOR: 'Front desk coordinator',
  HOSPITAL_TECHNICIAN: 'Station technician',
};

const isPhysician = (p: Principal | null | undefined) => Boolean(p?.roles?.includes('PHYSICIAN'));
/**
 * Somebody whose job is the bench, not the front desk. A lab technician
 * holds `encounter.station-enter` (they draw the sample at the collection
 * station), which is enough to reach the hospital console — and because that
 * console is listed first it became their home, so they signed in to the
 * reception queue instead of their samples.
 */
const isBenchOnly = (p: Principal | null | undefined) => {
  const roles = p?.roles ?? [];
  return roles.some((r) => r === 'LAB_TECHNICIAN' || r === 'PATHOLOGIST')
    && !roles.some((r) => r === 'HOSPITAL_COORDINATOR' || r === 'HOSPITAL_TECHNICIAN');
};

/**
 * Ordered most specific first, and that order is load-bearing for `landingFor`.
 *
 * The platform console comes first because a platform administrator holds a
 * very wide set — `appointment.checkin` among them — and any later console
 * would claim them before this one did.
 *
 * Roles overlap deliberately: a hospital coordinator holds `employee.read`
 * because they need to see who is at the desk, and a physician holds
 * `lab.worklist-read` because they read results. Matching on a permission a
 * clinical role happens to share would send both of them to the HR console.
 * So each console is identified by the permission that is genuinely its own —
 * signing a fitness decision, checking somebody in, releasing a result — and
 * the HR console is last because it is the general case.
 */
export const CONSOLES: Console[] = [
  /**
   * Operations — the lead inbox, and the front of every customer the platform
   * has.
   *
   * First in the list so an operator starts their day where their work
   * arrives rather than on a dashboard of companies that already exist. The
   * platform administrator holds the CRM too and reaches this from the
   * console switcher; `home` is what keeps them landing in their own.
   *
   * Internal, and outside every tenant scope (docs/00 B9). No customer and no
   * venue sees any of it, which the permissions and the row policies both
   * enforce independently of this list.
   */
  {
    key: 'ops', prefix: '/ops', label: 'Operations', icon: Inbox,
    need: ['lead.read', 'case.read-ops', 'case.read-support'],
    // Home for the people whose day is the case desk: Operations and
    // Support. Reading the lead inbox is not enough — Finance holds it too,
    // and landed on enquiries instead of the ledger.
    home: (p) => !p?.permissions?.includes('platform.impersonate')
      && !!p?.permissions?.some((x) => x === 'case.read-ops' || x === 'case.read-support'),
    sections: [
      { label: 'Daily', items: [
        // The case queue first: coordinating examinations is the daily work,
        // enquiries are the front door (docs/37 Step 3).
        { to: '/ops/cases', label: 'Cases', icon: Activity, need: 'case.read-ops' },
        { to: '/ops/leads', label: 'Leads', icon: Inbox, need: 'lead.read' },
      ] },
    ],
  },
  {
    key: 'admin', prefix: '/admin', label: 'Platform', icon: ShieldCheck,
    // The administrator, operations and finance all live here; the sections
    // below filter to what each holds. Finance holds none of the onboarding
    // acts, so the ledger permissions name the console for them.
    need: ['tenant.create', 'tenant.activate', 'partner.empanel', 'settlement.read', 'invoice.approve'],
    // The ledger is Finance's desk: somebody who raises invoices but neither
    // onboards companies nor works the case queue starts on Billing. The
    // pending payment submissions are one link away from there.
    start: (perms) => (holds(perms, 'invoice.create') && !holds(perms, ['tenant.create', 'case.read-ops'])
      ? '/admin/billing' : null),
    /**
     * Three sections, by how often they are opened rather than by which team
     * owns them: the work of every day, the screens opened a few times a
     * month, and the setup done once and then rarely touched.
     *
     * "Daily" is shared with the Operations console on purpose. The sidebar
     * merges sections by label, so an administrator who also holds the case
     * queue sees Cases beside Companies rather than under a second heading.
     */
    sections: [
      { label: 'Daily', items: [
        { to: '/admin/overview', label: 'Overview', icon: Gauge, need: 'crosstenant.tenant.read' },
        // Mounted in the Operations console; listed here too so that it sits
        // second for an administrator instead of trailing after the billing.
        { to: '/ops/cases', label: 'Cases', icon: Activity, need: 'case.read-ops' },
        { to: '/admin/companies', label: 'Companies', icon: Building2, need: 'tenant.read' },
        { to: '/admin/billing', label: 'Billing', icon: Banknote, need: 'invoice.create' },
        // What companies say they have paid, waiting for Finance to confirm.
        { to: '/admin/payments', label: 'Payment review', icon: Wallet, need: 'invoice.payment-confirm' },
        { to: '/admin/emergency', label: 'Emergency visits', icon: Siren, need: 'emergency.confirm' },
        // Enquiries: where a company or a venue comes from. Burying the inbox
        // in a separate console meant a platform administrator never saw a
        // lead arrive.
        { to: '/admin/leads', label: 'Leads', icon: Inbox, need: 'lead.read' },
      ] },
      { label: 'Occasional', items: [
        { to: '/admin/partners', label: 'Lab network', icon: Hospital, need: 'partner.read' },
        { to: '/admin/physicians', label: 'Physicians', icon: Stethoscope, need: 'examination.route' },
        // A physician asking to correct their own signed review. Approving
        // opens a re-review they sign; it never edits the record.
        { to: '/admin/review-corrections', label: 'Review corrections', icon: FilePenLine, need: 'review.correction-approve' },
      ] },
      { label: 'Setup', items: [
        { to: '/admin/masters', label: 'Clinical masters', icon: ListChecks, need: ['test.read', 'protocol.read'] },
        { to: '/admin/users', label: 'Platform users', icon: Users, need: 'user.read' },
        { to: '/admin/roles', label: 'Roles', icon: KeyRound, need: 'user.role-assign' },
        { to: '/admin/permissions', label: 'Permissions', icon: ShieldCheck, need: 'user.role-assign' },
        // Email (server and message wording) and Settings (status, integrations,
        // audit log, support contact) split apart on 29 Oct 2026.
        { to: '/admin/email', label: 'Email', icon: Mail, need: 'system.settings-manage' },
        { to: '/admin/settings', label: 'Settings', icon: SlidersHorizontal, need: ['system.settings-manage', 'audit.read'] },
      ] },
    ],
  },
  {
    key: 'physician', prefix: '/physician', label: 'Physician', icon: Stethoscope,
    need: ['review.queue-read', 'physician.certify'],
    // Three questions a physician asks, in the order they ask them: what is
    // waiting for me, what have I signed, and what has it earned.
    sections: [
      { label: 'Today', items: [
        { to: '/physician/queue', label: 'Review queue', icon: ClipboardCheck, need: 'review.queue-read' },
      ] },
      { label: 'History', items: [
        { to: '/physician/history', label: 'Review history', icon: History, need: 'review.queue-read' },
      ] },
      { label: 'Finance', items: [
        { to: '/physician/earnings', label: 'My earnings', icon: Wallet, need: ['settlement.read-own', 'staffpay.read-own'] },
      ] },
    ],
  },
  /**
   * One venue, one console.
   *
   * A hospital or laboratory is one organisation: the same people who see the
   * morning's queue accession the tubes, enter the values and answer for what
   * the place is paid. Splitting that across a Hospital console and a
   * Laboratory console made sense while a venue's account belonged to a
   * company and did one job in it; a venue with one login does not have two
   * rooms to be in, it has one place of work.
   *
   * The screens themselves are the same components the two older consoles
   * use. What changed is who reaches them and how many of them one person
   * sees at once.
   */
  {
    key: 'partner', prefix: '/partner', label: 'Venue', icon: Hospital,
    need: ['encounter.read', 'lab.worklist-read', 'appointment.read'],
    // Each role starts where its work is (brief §14), from permissions alone:
    // a pathologist on Validate & release; a technician — who cannot check
    // anyone in — on the samples; the front desk and the venue admin on the
    // queue (the first item). Admin status never opens a signing screen.
    start: (perms) => benchStart('/partner', perms, '/partner/samples', !holds(perms, 'appointment.checkin')),
    only: isVenueAccount,
    sections: [
      { label: 'Today', items: [
        { to: '/partner/queue', label: 'Queue', icon: CalendarCheck, need: 'encounter.read' },
        { to: '/partner/check-in', label: 'Check in', icon: IdCard, need: 'appointment.checkin' },
        { to: '/partner/emergency', label: 'Emergency visits', icon: Siren, need: ['emergency.record', 'emergency.confirm'] },
      ] },
      { label: 'Lab', items: [
        { to: '/partner/samples', label: 'Samples', icon: FlaskConical, need: 'lab.worklist-read' },
        { to: '/partner/results', label: 'Result entry', icon: Beaker, need: 'lab.result-enter' },
        {
          to: '/partner/release', label: 'Validate & release', icon: ShieldCheck,
          need: ['lab.result-validate', 'lab.result-release'],
        },
        { to: '/partner/reports', label: 'Reports', icon: FileText, need: 'lab.worklist-read' },
      ] },
      { label: 'Venue', items: [
        { to: '/partner/camps', label: 'Camps', icon: Tent, need: 'partner.update' },
        { to: '/partner/staff', label: 'Users', icon: Users, need: 'user.read' },
        { to: '/partner/profile', label: 'Profile', icon: Building2, need: 'partner.read' },
        { to: '/partner/earnings', label: 'Earnings', icon: Wallet, need: 'settlement.read-own' },
      ] },
      // Only for people whose work puts their signature on a report.
      { label: 'You', items: [
        { to: '/partner/signature', label: 'My signature', icon: PenLine, need: 'signature.manage-own' },
      ] },
    ],
  },
  {
    key: 'hospital', prefix: '/hospital', label: 'Hospital', icon: Hospital,
    need: ['appointment.checkin', 'encounter.station-enter', 'encounter.complete'],
    // Hidden from a venue account, which has the whole of this console and
    // the laboratory's inside `/partner`. Also hidden from physicians who have
    // their own dedicated Review console.
    only: (p) => !isVenueAccount(p) && !isPhysician(p),
    home: (p) => !isBenchOnly(p),
    sections: [
      { items: [
        { to: '/hospital/queue', label: 'Today’s queue', icon: CalendarCheck, need: 'encounter.read' },
        { to: '/hospital/check-in', label: 'Check in', icon: IdCard, need: 'appointment.checkin' },
      ] },
    ],
  },
  {
    key: 'lab', prefix: '/lab', label: 'Laboratory', icon: FlaskConical,
    need: ['lab.result-enter', 'lab.result-release', 'lab.sample-accession'],
    only: (p) => !isVenueAccount(p) && !isPhysician(p),
    // A pathologist starts on Validate & release; everyone else on Samples.
    start: (perms) => benchStart('/lab', perms, '/lab/worklist', true),
    sections: [
      { items: [
        { to: '/lab/worklist', label: 'Samples', icon: FlaskConical, need: 'lab.worklist-read' },
        { to: '/lab/results', label: 'Result entry', icon: Beaker, need: 'lab.result-enter' },
        {
          to: '/lab/release', label: 'Validate & release', icon: ShieldCheck,
          need: ['lab.result-validate', 'lab.result-release'],
        },
        // Assembling a report needs no more than entry rights; verifying and
        // signing it need the pathologist's, and the buttons on the screen are
        // gated individually. A technician who cannot sign still has to be
        // able to open the report and see what it is waiting on.
        { to: '/lab/reports', label: 'Reports', icon: FileText, need: ['lab.worklist-read'] },
        // Physicians asking the laboratory to verify a released value.
        { to: '/lab/verifications', label: 'Verification requests', icon: ShieldCheck, need: ['lab.worklist-read'] },
      ] },
      { label: 'You', items: [
        { to: '/lab/signature', label: 'My signature', icon: PenLine, need: 'signature.manage-own' },
      ] },
    ],
  },
  {
    key: 'hr', prefix: '/hr', label: 'HR & compliance', icon: UsersRound,
    // invoice.read is enough on its own: Plant Accounts is a company user
    // whose whole job is its plants' invoices (docs/37 Step 5), and without
    // it here that login landed on /no-console.
    need: ['compliance.read', 'employee.import', 'examination.create', 'invoice.read'],
    /**
     * Daily work first, configuration last.
     *
     * Order is load-bearing twice over: it is the order people read, and
     * `landingFor` sends each login to the first item it holds — Plant
     * Accounts reaches Billing before anything unconditional (Notifications)
     * precisely because Finance sits above Manage.
     */
    sections: [
      // The five screens of the daily work, and nothing else (31 Oct 2026):
      // a first-time HR user reads one short list. Adding and importing
      // people, and booking a checkup, are the sidebar's quick actions
      // (`QUICK_ACTIONS`) rather than a tree under Employees, so each is
      // offered once.
      { label: 'Workforce', items: [
        { to: '/hr/dashboard', label: 'Dashboard', icon: LayoutGrid, need: 'compliance.read' },
        { to: '/hr/employees', label: 'Employees', icon: UsersRound, need: 'employee.read' },
        { to: '/hr/examinations', label: 'Medical cases', icon: Activity, need: 'examination.read' },
        { to: '/hr/appointments', label: 'Appointments', icon: CalendarClock, need: 'appointment.read' },
        { to: '/hr/certificates', label: 'Certificates', icon: FileBadge, need: 'certificate.read' },
      ] },
      // The work that is not every day's: the first-aid room's register
      // (casualty, first aid, OPD) and company-wide checkup drives.
      // Treatments stays ahead of Campaigns so `landingFor` is unchanged.
      // Overview, Due list and Exports left the sidebar (owner, 26 Oct 2026);
      // their pages still open from the dashboard, cases and reports.
      { label: 'Health programmes', items: [
        { to: '/hr/treatments', label: 'Treatments', icon: BriefcaseMedical, need: 'treatment.read' },
        { to: '/hr/campaigns', label: 'Campaigns', icon: CalendarCheck, need: 'campaign.read' },
      ] },
      // Its own section, ahead of Manage: for Plant Accounts it is the whole
      // console, and for the Company Admin it is not an admin task.
      { label: 'Finance', items: [
        { to: '/hr/billing', label: 'Billing', icon: Banknote, need: 'invoice.read', children: [
          { to: '/hr/billing', label: 'Overview', end: true },
          { to: '/hr/billing/invoices', label: 'Invoices' },
        ] },
        { to: '/hr/emergency-bills', label: 'Emergency bills', icon: Siren, need: 'emergency.read' },
      ] },
      // No setup screens: the company, its plants and its medical package are
      // configured by Dr Ultimate staff (docs/38). The old addresses redirect
      // (router.tsx) and the API refuses the writes.
      { label: 'Manage', items: [
        { to: '/hr/users', label: 'Users', icon: Users, need: 'user.create' },
        // The company's own banners on its workers' home screens.
        { to: '/hr/notifications', label: 'Notifications', icon: Bell },
        { to: '/hr/settings', label: 'Settings', icon: SlidersHorizontal, need: 'settings.read' },
      ] },
    ],
  },
];

/**
 * What a console is called for this person.
 *
 * The company console is "HR & compliance" for the people who run the
 * workforce. A login that reaches it only through `invoice.read` — Plant
 * Accounts — does none of that work, so it reads as billing instead. Decided
 * by what the login holds, not by its role's name.
 */
export function consoleLabel(c: Console, permissions: ReadonlySet<string>): string {
  if (c.key === 'hr' && !holds(permissions, ['compliance.read', 'employee.import', 'examination.create'])) {
    return 'Billing & Accounts';
  }
  return c.label;
}

export function holds(permissions: ReadonlySet<string>, need: string | string[]): boolean {
  const list = Array.isArray(need) ? need : [need];
  return list.length === 0 || list.some((p) => permissions.has(p));
}

/** Every item across every section, for the landing and breadcrumb lookups. */
function allItems(c: Console): NavItem[] {
  return c.sections.flatMap((s) => s.items);
}

/**
 * Which realm a console belongs to.
 *
 * Not decoration, and not a grouping chosen for the look of the sidebar. These
 * are the three scopes the data model itself is built on — `GLOBAL`, `TENANT`
 * and `PARTNER` in the role table — and they decide whose records a screen
 * reads:
 *
 *   platform  the platform and its own staff. Cross-tenant by nature, and
 *             deliberately holds no customer's clinical records.
 *   company   ONE company's own workforce, examinations and certificates.
 *   venue     ONE hospital or laboratory, and the people who work in it.
 *
 * The distinction is load-bearing rather than tidy. `PLATFORM_ADMIN` holds
 * `employee.read`, `compliance.read` and most of the rest of the catalogue,
 * because the seed requires an administrator to hold everything it can grant
 * to somebody else. Permission overlap therefore CANNOT be used to decide what
 * belongs in a platform sidebar: read literally it puts a customer's workforce
 * under the platform's own navigation, which is precisely the reach the
 * oversight screen exists to make deliberate, audited and reason-gated.
 *
 * So the realm is what separates them, and a session only ever shows the realm
 * it is standing in.
 */
export type Realm = 'platform' | 'company' | 'venue';

const REALM_OF: Record<string, Realm> = {
  ops: 'platform',
  admin: 'platform',
  hr: 'company',
  physician: 'company',
  partner: 'venue',
  hospital: 'venue',
  lab: 'venue',
};

/**
 * The same three realms, as the role table's own scope types.
 *
 * A role's reach is what says whose people hold it: GLOBAL is the platform's
 * own staff, TENANT and PLANT are a company's, PARTNER is a venue's. The roles
 * screen is a list of roles somebody administers, so it is bounded the same way
 * the navigation is — a platform administrator manages the platform's own
 * roles, and a company's HR roles belong in that company's own console.
 */
export const REALM_OF_SCOPE: Record<string, Realm> = {
  GLOBAL: 'platform',
  TENANT: 'company',
  PLANT: 'company',
  CAMP: 'company',
  PARTNER: 'venue',
};

/**
 * The roles whose realm their scope does not give away.
 *
 * `PHYSICIAN` is SELF-scoped, because a certifying surgeon reads their own
 * queue and nobody else's — but the panel they belong to is the PLATFORM's.
 * Surgeons are empanelled centrally and serve every company on the platform,
 * which is why the certifying panel is a platform screen and why the company
 * and venue user pages both list `PHYSICIAN` among the roles that are NOT
 * theirs to grant. So it is administered from the platform, with the platform's
 * own roles.
 *
 * `EMPLOYEE` stays out of every list. It is SELF-scoped and holds no permission
 * at all: a worker signs in to the separate PWA, and the role is a consequence
 * of being on a company's roll rather than something an administrator hands
 * out on a roles screen.
 */
const REALM_OF_ROLE: Record<string, Realm> = {
  PHYSICIAN: 'platform',
};

const UNLISTED_ROLES = new Set(['EMPLOYEE']);

/**
 * Which realm's roles screen a role belongs on, or null for one that belongs
 * on none.
 */
export function realmOfRole(role: { key: string; scopeType: string }): Realm | null {
  if (UNLISTED_ROLES.has(role.key)) return null;
  return REALM_OF_ROLE[role.key] ?? REALM_OF_SCOPE[role.scopeType] ?? null;
}

/**
 * Which realm this principal actually belongs to.
 *
 * Read from the account rather than from its permissions, for the reason
 * above. A venue account carries a `partner` and no company; a company's user
 * carries a `tenant`; the platform's own staff carry neither and hold a GLOBAL
 * scope. That is the same three-way split the API's row policies enforce, so
 * the navigation cannot disagree with what the server will actually return.
 */
export function realmOf(principal: Principal | null | undefined): Realm {
  if (principal?.partner) return 'venue';

  // The older venue accounts predate a venue having a login of its own: they
  // are PARTNER-scoped roles bound to a company, with no `partner` on the
  // principal. Going by `tenant` alone would put a bench technician in the
  // company realm and leave them with no console at all, so the roles they
  // hold decide it. A newer venue account carries `partner` and never reaches
  // this line.
  if (principal?.roles?.some((r) => VENUE_ROLES.has(r))) return 'venue';

  // A panel physician's login carries no company either (20261020000500 —
  // one login reviews every company's cases assigned to it), and is not
  // platform staff. Their console is the one it always was; without this the
  // sidebar was empty and "/" had nowhere to go.
  if (isPhysician(principal)) return REALM_OF.physician!;

  if (principal?.tenant) return 'company';
  return 'platform';
}

/** The PARTNER-scoped roles, as the role table defines them. */
const VENUE_ROLES = new Set([
  'PARTNER_ADMIN', 'LAB_TECHNICIAN', 'HOSPITAL_COORDINATOR',
  'HOSPITAL_TECHNICIAN', 'PATHOLOGIST',
]);

/**
 * One sidebar, holding everything this person may reach WITHIN their realm.
 *
 * Every console of the realm is concatenated, so a platform administrator sees
 * the lead inbox, the registry, the ledger and the governance screens as one
 * list rather than finding half of them under a footer heading called "other
 * consoles". What they do not see is a company's workforce: that is a
 * different realm, reached through the audited oversight path and nowhere
 * else.
 *
 * A console's own sections keep their labels. Where a console's first section
 * is unlabelled — Operations is a single unlabelled item, and so is the
 * physician's queue — it takes the console's name, because an unlabelled group
 * in the middle of a merged list has nothing to say what it is. Headings are
 * merged rather than repeated: Operations exists as a section of the platform
 * console AND as a console of its own, and printing it twice reads as a bug.
 */
/**
 * The consoles this person may reach, bounded by realm before permissions are
 * consulted at all — see `realmOf`.
 */
export function reachableConsoles(permissions: ReadonlySet<string>, principal?: Principal | null): Console[] {
  const realm = realmOf(principal);
  const reachable = CONSOLES.filter(
    (c) => REALM_OF[c.key] === realm
      && holds(permissions, c.need)
      && (!c.only || c.only(principal)),
  );
  // The console somebody starts their day in leads their sidebar — the same
  // rule `landingFor` applies. Sections merge by label, so without this an
  // administrator's "Daily" opened with the Operations console's queue and
  // put their own Overview fourth.
  const home = reachable.find((c) => !c.home || c.home(principal)) ?? reachable[0];
  return home ? [home, ...reachable.filter((c) => c !== home)] : reachable;
}

/** The sidebar this person sees: every item they may reach within their realm. */
export function navFor(permissions: ReadonlySet<string>, principal?: Principal | null): NavSection[] {
  return mergedSections(reachableConsoles(permissions, principal), permissions);
}

function mergedSections(reachable: Console[], permissions: ReadonlySet<string>): NavSection[] {
  const out: NavSection[] = [];
  const byLabel = new Map<string, NavSection>();
  const seen = new Set<string>();

  for (const c of reachable) {
    for (const section of c.sections) {
      const items = section.items.filter((it) => {
        if (it.need && !holds(permissions, it.need)) return false;
        // The lead inbox is mounted in two consoles on purpose (`useLeadBase`),
        // so a merged list would otherwise show it twice under two headings.
        if (seen.has(it.label)) return false;
        seen.add(it.label);
        return true;
      }).map((it) => (it.children
        ? { ...it, children: it.children.filter((ch) => !ch.need || holds(permissions, ch.need)) }
        : it));
      if (items.length === 0) continue;

      const label = section.label ?? c.label;
      const existing = byLabel.get(label);
      if (existing) {
        existing.items = [...existing.items, ...items];
        continue;
      }
      const created: NavSection = { label, items };
      byLabel.set(label, created);
      out.push(created);
    }
  }
  return out;
}

/**
 * Where "/" goes.
 *
 * Not a fixed destination: a hospital coordinator sent to the HR dashboard
 * meets a 403 as their first impression of the product, which teaches people
 * that errors are normal — the same reason the navigation is permission-
 * filtered at all.
 */
/**
 * Where a laboratory person starts: a pathologist (validates or releases) on
 * Validate & release; a bench technician on the samples when `benchFirst`;
 * otherwise null, and the console's first item decides.
 */
export function benchStart(prefix: '/lab' | '/partner', perms: ReadonlySet<string>, samples: string, benchFirst: boolean): string | null {
  if (holds(perms, ['lab.result-validate', 'lab.result-release'])) return `${prefix}/release`;
  if (benchFirst && holds(perms, 'lab.worklist-read')) return samples;
  return null;
}

export function landingFor(permissions: ReadonlySet<string>, principal?: Principal | null): string {
  // Same realm bound as the sidebar, and for the same reason: a platform
  // administrator holds `compliance.read`, so an unbounded search lands them
  // in a customer's HR dashboard rather than their own console.
  const realm = realmOf(principal);
  const reachable = CONSOLES.filter(
    (c) => REALM_OF[c.key] === realm
      && holds(permissions, c.need)
      && (!c.only || c.only(principal)),
  );
  // The console whose `home` claims this person; failing that, the first
  // they can reach at all — a console that is nobody's home is still better
  // than a page saying there is none.
  const home = reachable.find((c) => !c.home || c.home(principal)) ?? reachable[0];
  // Nobody is sent to a console they cannot read. A 403 as the first screen
  // teaches people that errors are normal; a page that says what is missing
  // does not.
  if (!home) return '/no-console';
  const start = home.start?.(permissions);
  if (start) return start;
  const items = allItems(home);
  const first = items.find((i) => !i.need || holds(permissions, i.need));
  return first?.to ?? items[0]!.to;
}

// v2: the first key was written on every visit, so its '0' was never a choice.
const COLLAPSE_KEY = 'dru.nav.collapsed.v2';

export function ConsoleShell() {
  return (
    <BreadcrumbProvider>
      <ConsoleShellInner />
    </BreadcrumbProvider>
  );
}

function ConsoleShellInner() {
  const { principal, permissions, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState(false);
  const [viewPlant, setViewPlant] = useState<string | null>(storedViewPlant);
  // Remembered, because somebody who works all day in a wide table collapses
  // the navigation once and should not have to do it again every morning.
  // Until they choose, a tablet in landscape (below xl) starts with the icon
  // rail: a full sidebar leaves the page about 750px, and pages laid out for
  // a desktop squeeze into three thin columns.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(COLLAPSE_KEY);
      if (v === '1' || v === '0') return v === '1';
    } catch { /* storage blocked: fall through to the width */ }
    return typeof window !== 'undefined' && window.innerWidth < 1280;
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* not remembered */ }
    return !c;
  });

  // Navigating closes the drawer; leaving it open over the page somebody just
  // asked for is the most common mobile navigation bug there is.
  useEffect(() => { setDrawer(false); }, [pathname]);

  // The drawer is a modal on a phone: focus goes into it, Escape closes it,
  // and closing hands focus back to the button that opened it — otherwise a
  // keyboard or switch user is left on a control hidden behind the scrim.
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!drawer) return undefined;
    const opener = menuButton.current;
    drawerRef.current?.querySelector<HTMLElement>('nav a[href], nav button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [drawer]);

  // The console is decided by where you are, not by which role sorts first —
  // a physician who also holds a hospital binding is in whichever room the
  // URL says.
  const active = CONSOLES.find((c) => pathname.startsWith(c.prefix))
    ?? CONSOLES.find((c) => c.key === 'hr')!;
  // Bounded by realm before permissions are consulted at all. A platform
  // administrator holds most of the catalogue — including `employee.read` —
  // so filtering on permissions alone would put a customer's workforce in the
  // platform's own navigation. Whose records a screen reads is decided by the
  // account, not by the breadth of what it may do.
  const reachable = reachableConsoles(permissions, principal);

  // The organisation this session works inside: a company's user sees its
  // name, a venue account its venue's. The platform's own staff have neither.
  //
  // A certifying physician is on the platform's panel and reviews for every
  // company that routes a case to them; the account happens to be seated in
  // one. "Company: X" beside their name told them — wrongly — whose workers
  // their queue holds, so they get no chip.
  const context: HeaderContext | null = principal?.partner
    ? { kind: 'Venue', name: principal.partner.name, icon: Hospital }
    : principal?.tenant && !isPhysician(principal)
      ? { kind: 'Company', name: principal.tenant.name, icon: Building2 }
      : null;
  // Every console has its own notifications page; the bell opens this one's.
  const inbox = `${active.prefix}/notifications`;

  async function signOut() {
    setSigningOut(true);
    await logout();
    nav('/login', { replace: true });
  }

  const sidebar = (
    <Sidebar
      permissions={permissions}
      collapsed={collapsed}
      onToggle={toggleCollapsed}
      reachable={reachable}
    />
  );

  // The header's employee search: HR only, and only for a login that can read employees.
  const findWorkers = active.prefix === '/hr' && holds(permissions, 'employee.read');
  // Platform staff find anyone from the header (owner, 30 Oct 2026): it
  // replaced the Support desk page.
  const findAnything = (active.prefix === '/admin' || active.prefix === '/ops')
    && !principal?.tenant && !principal?.partner && !principal?.impersonated;
  const headerSearch = findWorkers || findAnything;
  // The plant switcher: HR, a company-wide login (not one already held to a
  // plant), which can read plants. Its choice rides on every read the API
  // client makes; the page remounts on a switch so everything refetches.
  const companyWide = !!principal?.scopes?.some((x) => x.scopeType === 'TENANT')
    && !principal?.scopes?.some((x) => x.scopeType === 'PLANT');
  const switchPlants = active.prefix === '/hr' && companyWide && holds(permissions, 'plant.read');
  const viewing = switchPlants ? viewPlant : null;
  if (api.getViewPlant() !== viewing) api.setViewPlant(viewing);

  // The layout of 29 Oct 2026: the sidebar runs the full height with the logo
  // at its top, and the header sits beside it rather than across the page.
  return (
    <div className="flex min-h-screen bg-canvas">
      <div className="hidden lg:block">{sidebar}</div>

      <div className="flex min-w-0 flex-1 flex-col">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white">
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            type="button"
            ref={menuButton}
            onClick={() => setDrawer(true)}
            aria-label="Open navigation"
            aria-expanded={drawer}
            className="-ml-1 rounded-btn p-2 text-neutral-600 hover:bg-neutral-100 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex min-w-0 items-center gap-2">
            {/* From lg up the logo heads the sidebar; below, the sidebar is a
                drawer, so the header carries it. */}
            <img
              src={BRAND.logo.mark}
              alt={BRAND.logo.markAlt}
              className="logo-plate h-7 w-7 shrink-0 object-contain sm:hidden"
            />
            <img
              src={BRAND.logo.full}
              alt={BRAND.logo.alt}
              className="logo-plate hidden h-7 shrink-0 object-contain sm:block lg:hidden"
            />
            {/* No console name and no "Company:" label (owner, 26 Oct 2026):
                the logo, then whose records these are, by name alone. */}
            {/*
              Whose records these are. The venue consoles show two
              organisations at once — the company whose workers are being
              examined and the hospital examining them — and an unlabelled name
              beside a venue picker reads as a second venue. So the chip says
              what it is ("Company:", "Venue:") rather than going away.
            */}
            {context && (
              <span className="hidden min-w-0 items-center gap-1 rounded-badge border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-caption text-neutral-800 md:inline-flex">
                <context.icon aria-hidden className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
                <span className="truncate" title={`${context.kind}: ${context.name}`}>{context.name}</span>
              </span>
            )}
          </div>

          {/*
            Find a worker from any HR screen: the employee ID is what HR has in
            hand, so it is a box in the header and not a trip to the list.
          */}
          {findWorkers && (
            <EmployeeSearch className="ml-auto hidden w-full max-w-sm md:block" />
          )}
          {findAnything && (
            <PlatformSearch className="ml-auto hidden w-full max-w-md md:block" prefixes={reachable.map((c) => c.prefix)} />
          )}

          <div className={cn('flex items-center gap-1.5 sm:gap-2', headerSearch ? 'ml-auto md:ml-2' : 'ml-auto')}>
            {switchPlants && (
              <PlantSwitcher
                value={viewPlant}
                onChange={(id) => { storeViewPlant(id); api.setViewPlant(id); setViewPlant(id); }}
                className="hidden sm:flex"
              />
            )}
            {headerSearch && (
              <button
                type="button"
                onClick={() => setPhoneSearch((v) => !v)}
                aria-label={findAnything ? 'Search' : 'Search employees'}
                aria-expanded={phoneSearch}
                className="rounded-btn p-2 text-neutral-600 hover:bg-neutral-100 md:hidden"
              >
                <Search className="h-5 w-5" />
              </button>
            )}
            <NotificationCenter inbox={inbox} prefixes={reachable.map((c) => c.prefix)} />
            <UserMenu
              principal={principal}
              context={context}
              securityTo={`${active.prefix}/security`}
              signingOut={signingOut}
              onSignOut={signOut}
            />
          </div>
        </div>
        {headerSearch && phoneSearch && (
          <div className="border-t border-neutral-100 px-4 py-2 md:hidden">
            {findAnything
              ? <PlatformSearch className="w-full" autoFocus prefixes={reachable.map((c) => c.prefix)} onNavigate={() => setPhoneSearch(false)} />
              : <EmployeeSearch className="w-full" autoFocus onNavigate={() => setPhoneSearch(false)} />}
          </div>
        )}
      </header>

      {principal?.impersonated && <ImpersonationBanner principal={principal} />}

      <div className="flex min-w-0 flex-1">
        {drawer && (
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="fixed inset-0 z-40 lg:hidden"
          >
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setDrawer(false)}
              className="absolute inset-0 bg-neutral-950/40 animate-fade-in"
            />
            <div className="absolute inset-y-0 left-0 flex animate-slide-up">
              <Sidebar
                permissions={permissions}
                collapsed={false}
                reachable={reachable}
                onClose={() => setDrawer(false)}
              />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6">
            {switchPlants && (
              <PlantSwitcher
                value={viewPlant}
                onChange={(id) => { storeViewPlant(id); api.setViewPlant(id); setViewPlant(id); }}
                className="mb-3 sm:hidden"
              />
            )}
            <BreadcrumbNav active={active} />
            {/* Keyed on the viewed plant: switching remounts the page, so every
                list and count on it is fetched again for that plant. */}
            <div key={viewing ?? 'all-plants'}><Outlet /></div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}

function getSingularLabel(label: string): string {
  if (label === 'Companies') return 'Company';
  if (label === 'Lab network') return 'Lab';
  if (label === 'Medical cases') return 'Medical case';
  if (label.endsWith('ies')) return label.slice(0, -3) + 'y';
  if (label.endsWith('s')) return label.slice(0, -1);
  return label;
}

function defaultDetailLabel(to: string, listLabel: string): string {
  if (to === '/admin/partners') return 'Lab details';
  if (to === '/admin/companies') return 'Company details';
  if (to === '/admin/leads' || to === '/ops/leads') return 'Lead details';
  if (to === '/admin/users') return 'User details';
  if (to === '/admin/physicians') return 'Physician details';
  if (to === '/admin/invoices' || to === '/hr/billing') return 'Invoice details';
  if (to === '/hr/employees') return 'Employee details';
  if (to === '/hr/examinations') return 'Case details';
  if (to === '/hr/appointments') return 'Appointment details';
  if (to === '/hr/certificates') return 'Certificate details';
  if (to === '/hr/campaigns') return 'Campaign details';
  if (to === '/partner/encounter' || to === '/hospital/encounter') return 'Encounter details';
  if (to === '/physician/review') return 'Review details';
  if (to === '/physician/history') return 'Signed review';
  const singular = getSingularLabel(listLabel);
  return `${singular} details`;
}

function BreadcrumbNav({ active }: { active: Console }) {
  const { pathname } = useLocation();
  const { customTitle, customCrumbs } = useContext(BreadcrumbContext);

  if (customCrumbs && customCrumbs.length > 0) {
    return renderBreadcrumbs(customCrumbs);
  }

  const current = allItems(active)
    .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  const deeper = current ? pathname.slice(current.to.length).split('/').filter(Boolean) : [];

  // No console root ("Platform", "Operations"): the sidebar already says which
  // console this is, so the trail starts at the page.
  const crumbs: BreadcrumbItem[] = [];

  if (current) {
    if (deeper.length === 0) {
      crumbs.push({
        label: current.label,
      });
    } else {
      crumbs.push({
        label: current.label,
        to: current.to,
      });

      if (current.to === '/admin/masters' && deeper.length >= 1) {
        const masterTabNames: Record<string, string> = {
          tests: 'Tests',
          hazards: 'Hazards',
          protocols: 'Protocols',
          packages: 'Packages',
          panels: 'Panels',
          restrictions: 'Restrictions',
          industries: 'Industries',
        };
        const tabKey = deeper[0]!;
        const tabLabel = masterTabNames[tabKey] ?? (tabKey.charAt(0).toUpperCase() + tabKey.slice(1));

        if (deeper.length === 1) {
          crumbs.push({
            label: tabLabel,
          });
        } else {
          crumbs.push({
            label: tabLabel,
            to: `${current.to}?tab=${tabKey}`,
          });

          const singular = tabLabel.endsWith('s') ? tabLabel.slice(0, -1) : tabLabel;
          crumbs.push({
            label: customTitle || (deeper[1] === 'new' ? `New ${singular.toLowerCase()}` : `${singular} details`),
          });
        }
      } else if (current.to === '/admin/companies' && deeper.length >= 2 && deeper[1] === 'oversight') {
        crumbs.push({
          label: customTitle || 'Company details',
          to: `${current.to}/${deeper[0]}`,
        });
        crumbs.push({
          label: 'Privileged access',
        });
      } else if (current.to === '/hr/billing' && deeper.length === 1 && deeper[0] === 'invoices') {
        // A tab of the billing screen, not an invoice id.
        crumbs.push({ label: 'Invoices' });
      } else if (deeper[0] === 'new') {
        const singular = getSingularLabel(current.label);
        crumbs.push({
          label: `New ${singular.toLowerCase()}`,
        });
      } else if (deeper[0] === 'register') {
        crumbs.push({
          label: current.to === '/admin/partners' ? 'Register a network partner' : 'Register',
        });
      } else if (deeper[0] === 'import') {
        crumbs.push({
          label: `Import ${current.label.toLowerCase()}`,
        });
      } else {
        crumbs.push({
          label: customTitle || defaultDetailLabel(current.to, current.label),
        });
      }
    }
  }

  if (crumbs.length <= 1) {
    return null;
  }

  return renderBreadcrumbs(crumbs);
}

function renderBreadcrumbs(crumbs: BreadcrumbItem[]) {
  const previousCrumb = crumbs.length >= 2 ? crumbs[crumbs.length - 2] : null;

  return (
    // The way back sits at the right end of the trail as a labelled button,
    // where it is found without reading the trail first.
    <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-3">
      <ol className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-caption font-normal text-neutral-500">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={idx} className="inline-flex items-center gap-1.5">
              {idx > 0 && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-neutral-300" aria-hidden="true" />
              )}
              {isLast || !crumb.to ? (
                <span
                  className={cn(
                    'truncate max-w-xs sm:max-w-md md:max-w-lg',
                    isLast ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-600',
                  )}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className={cn('inline-flex min-h-6 items-center font-medium text-neutral-500 hover:text-neutral-900 hover:underline transition-colors', TAP_AREA)}
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      {previousCrumb?.to && (
        <Link
          to={previousCrumb.to}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1 rounded-btn border border-neutral-300 bg-white pl-2 pr-3 text-small font-medium text-neutral-700 transition-colors hover:bg-neutral-50 max-md:h-10',
          )}
          title={`Back to ${previousCrumb.label}`}
          aria-label={`Back to ${previousCrumb.label}`}
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          Back
        </Link>
      )}
    </nav>
  );
}

interface HeaderContext {
  kind: 'Company' | 'Venue';
  name: string;
  icon: LucideIcon;
}


/**
 * Who is signed in, and the three things they do with that: their security
 * settings, their theme, and signing out.
 *
 * One control instead of four. The header had a name, a role line, a theme
 * switch, a Security button and a Sign out button competing with the console
 * name for the same strip — and on a laptop the company name was the thing
 * that got squeezed out. A disclosure rather than an ARIA menu: it holds a
 * link, a radio group and a button, which a `role="menu"` would misdescribe.
 */
function UserMenu({ principal, context, securityTo, signingOut, onSignOut }: {
  principal: Principal | null | undefined;
  context: HeaderContext | null;
  securityTo: string;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const panelId = useId();
  // Only people whose work puts a signature on a report have one to manage.
  const canSign = useAuth().permissions.has('signature.manage-own');

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = principal?.fullName ?? '';
  // The venue's older roles are still named "Hospital Coordinator" and
  // "Hospital Technician (legacy)" in the database, while the venue's own
  // Staff screen calls the same people what they are. One name per job: the
  // header uses the Staff screen's.
  const roles = principal?.roles?.length
    ? principal.roles.map((key, i) => VENUE_ROLE_NAME[key] ?? principal.roleNames?.[i] ?? key).join(', ')
    : (principal?.roleNames ?? []).join(', ');
  const initials = name.split(/\s+/).filter((w) => /^[A-Za-z]/.test(w) && !/^Dr\.?$/i.test(w))
    .slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Account: ${name}`}
        title={name}
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full hover:bg-neutral-100',
          open && 'bg-neutral-100',
        )}
      >
        <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-caption font-semibold text-primary-700">
          {initials}
        </span>
        {/* Just the initials (owner, 26 Oct 2026); name, role and the
            options are in the menu it opens. */}
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-40 mt-1.5 w-72 rounded-card border border-neutral-200 bg-white shadow-dropdown animate-fade-in"
        >
          <div className="border-b border-neutral-200 px-4 py-3">
            <p className="truncate text-body font-semibold text-neutral-900">{name}</p>
            {principal?.email && <p className="truncate text-caption font-normal text-neutral-500">{principal.email}</p>}
            {roles && <p className="mt-1 text-caption text-neutral-700">{roles}</p>}
            {context && (
              <p className="mt-1 truncate text-caption font-normal text-neutral-500">
                {context.kind}: <span className="text-neutral-800">{context.name}</span>
              </p>
            )}
          </div>
          <div className="p-1.5">
            <Link
              to={securityTo}
              className="flex items-center gap-2.5 rounded-btn px-2.5 py-2 text-body text-neutral-800 hover:bg-neutral-100"
            >
              <ShieldCheck aria-hidden className="h-4 w-4 text-neutral-500" /> Security
            </Link>
            {canSign && (
              <Link
                to={securityTo.replace(/\/security$/, '/signature')}
                className="flex items-center gap-2.5 rounded-btn px-2.5 py-2 text-body text-neutral-800 hover:bg-neutral-100"
              >
                <PenLine aria-hidden className="h-4 w-4 text-neutral-500" /> My signature
              </Link>
            )}
          </div>
          <div className="border-t border-neutral-200 px-4 py-3">
            <div className="pb-1.5 text-micro uppercase tracking-wider text-neutral-500">Appearance</div>
            <ThemeToggle className="w-full justify-between" />
          </div>
          <div className="border-t border-neutral-200 p-1.5">
            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              className="flex w-full items-center gap-2.5 rounded-btn px-2.5 py-2 text-left text-body text-neutral-800 hover:bg-neutral-100 disabled:text-neutral-400"
            >
              <LogOut aria-hidden className="h-4 w-4 text-neutral-500" />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The banner a Super Admin sees while signed in as somebody else: who, and
 * Stop, which restores their own session and takes them back to the page
 * they started from. Nothing is shown to the person signed into.
 */
function ImpersonationBanner({ principal }: { principal: Principal }) {
  const { endImpersonation } = useAuth();
  const nav = useNavigate();
  const [ending, setEnding] = useState(false);

  async function stop() {
    setEnding(true);
    let back = '/admin/overview';
    try { back = sessionStorage.getItem('dru.impersonation.return') || back; sessionStorage.removeItem('dru.impersonation.return'); } catch { /* the overview */ }
    try {
      await endImpersonation();
      nav(back, { replace: true });
    } finally { setEnding(false); }
  }

  return (
    <div role="status" className="sticky top-14 z-20 border-b border-warning-600/30 bg-warning-100 px-4 py-1.5 text-caption text-warning-600">
      {/* Stop sits beside the name, not at the far right where the
          notification toasts cover it. */}
      <div className="mx-auto flex max-w-[1600px] items-center gap-3">
        <span className="inline-flex min-w-0 items-center gap-1.5 font-semibold">
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">
            Signed in as {principal.fullName}{principal.email ? ` (${principal.email})` : ''} — impersonating
          </span>
        </span>
        <button
          type="button"
          onClick={stop}
          disabled={ending}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-btn border border-warning-600/40 bg-white px-2.5 font-medium text-warning-600 hover:bg-warning-100 disabled:opacity-60 [@media(pointer:coarse)]:h-10"
        >
          <X className="h-3.5 w-3.5" aria-hidden /> {ending ? 'Stopping…' : 'Stop'}
        </button>
      </div>
    </div>
  );
}

/**
 * The three things a company user starts most often, one tap from anywhere
 * in the HR console. Links, not navigation items: they carry no "you are
 * here" state, so they never highlight beside the page they open.
 * `?arrange=1` opens the booking dialog on Medical cases.
 */
const QUICK_ACTIONS: Array<{ to: string; label: string; icon: LucideIcon; need: string }> = [
  { to: '/hr/examinations?arrange=1', label: 'Schedule checkup', icon: CalendarPlus, need: 'examination.create' },
  { to: '/hr/employees/new', label: 'Add employee', icon: UserPlus, need: 'employee.create' },
  { to: '/hr/employees/import', label: 'Import employees', icon: Upload, need: 'employee.import' },
];

function QuickActions({ permissions, collapsed, onPick }: {
  permissions: ReadonlySet<string>;
  collapsed: boolean;
  onPick: () => void;
}) {
  const { pathname } = useLocation();
  // On Medical cases itself the page's own "Schedule checkup" button is the
  // one to use: the page reads ?arrange=1 only when it opens.
  const actions = QUICK_ACTIONS.filter((a) => holds(permissions, a.need) && a.to.split('?')[0] !== pathname);
  if (actions.length === 0) return null;
  return (
    <div className={cn(collapsed ? 'px-2 pt-3' : 'px-3 pt-3')}>
      {!collapsed && (
        <div className="px-3 pb-1.5 text-micro font-medium uppercase tracking-wider text-neutral-500">Quick actions</div>
      )}
      <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
        {actions.map((a) => (
          <li key={a.to}>
            <Link
              to={a.to}
              onClick={onPick}
              title={collapsed ? a.label : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-btn text-body font-medium text-primary-700 transition-colors duration-hover',
                'hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 max-md:min-h-10',
                collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
              )}
            >
              <a.icon aria-hidden className="h-5 w-5 shrink-0 text-primary-600" strokeWidth={2} />
              {collapsed ? <span className="sr-only">{a.label}</span> : <span className="truncate">{a.label}</span>}
            </Link>
          </li>
        ))}
      </ul>
      {/* Collapsed there are no headings: a hairline keeps the actions apart from the pages. */}
      {collapsed && <div aria-hidden className="mx-2 mt-2 border-t border-neutral-200" />}
    </div>
  );
}

function Sidebar({ permissions, collapsed, onToggle, onClose, reachable }: {
  permissions: ReadonlySet<string>;
  collapsed: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  reachable: Console[];
}) {
  const sections = mergedSections(reachable, permissions);
  // An item whose path is the parent of another item's matches only itself:
  // on /hr/compliance/due both "Compliance" and "Due list" were highlighted.
  const allTo = sections.flatMap((s) => s.items.map((it) => it.to));
  const isParent = (to: string) => allTo.some((t) => t !== to && t.startsWith(`${to}/`));

  // Find a page by name. Filters this person's own menu, so it can never
  // offer a page they may not open; ⌘K / Ctrl+K jumps to it from anywhere.
  const [query, setQuery] = useState('');
  const { pathname } = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (collapsed) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [collapsed]);
  const q = query.trim().toLowerCase();
  const shown = q
    ? sections.map((s) => ({ ...s, items: s.items.filter((it) => it.label.toLowerCase().includes(q)) }))
    : sections;
  const firstMatch = q ? shown.flatMap((s) => s.items)[0] : undefined;
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex h-screen shrink-0 flex-col border-r border-neutral-200 bg-white',
        'sticky top-0 transition-[width] duration-page',
        collapsed ? 'w-[72px]' : 'w-64',
      )}
    >
      {/* The logo heads the sidebar, with the control that folds it. */}
      <div className={cn('flex h-14 shrink-0 items-center border-b border-neutral-100', collapsed ? 'justify-center px-2' : 'gap-2 px-4')}>
        {collapsed
          ? <img src={BRAND.logo.mark} alt={BRAND.logo.markAlt} className="logo-plate h-8 w-8 object-contain" />
          : <img src={BRAND.logo.full} alt={BRAND.logo.alt} className="logo-plate h-8 min-w-0 object-contain object-left" />}
        {!collapsed && onToggle && (
          <button type="button" onClick={onToggle} aria-expanded aria-label="Collapse navigation" title="Collapse"
                  className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-btn border border-neutral-200 text-neutral-500 hover:bg-neutral-100">
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close navigation"
                  className="ml-auto rounded-btn p-1.5 text-neutral-600 hover:bg-neutral-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {collapsed && onToggle && (
        <button type="button" onClick={onToggle} aria-expanded={false} aria-label="Expand navigation" title="Expand"
                className="mx-auto mt-3 flex h-8 w-8 items-center justify-center rounded-btn border border-neutral-200 text-neutral-500 hover:bg-neutral-100">
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {!collapsed && (
        <div className="px-3 pt-3">
          <label className="flex h-10 items-center gap-2 rounded-btn border border-neutral-200 bg-neutral-50 px-3 text-neutral-500 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && firstMatch) { navigate(firstMatch.to); setQuery(''); onClose?.(); }
                if (e.key === 'Escape') setQuery('');
              }}
              placeholder="Search"
              aria-label="Find a page"
              className="min-w-0 flex-1 bg-transparent text-body text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
            />
            <kbd className="hidden shrink-0 rounded border border-neutral-200 bg-white px-1.5 text-micro text-neutral-500 lg:inline">
              {isMac ? '⌘' : 'Ctrl'} K
            </kbd>
          </label>
        </div>
      )}

      {/* HR only, and only while the reader is in the HR console. Hidden
          while searching, so the results are the only list on screen. */}
      {!q && pathname.startsWith('/hr') && reachable.some((c) => c.key === 'hr') && (
        <QuickActions permissions={permissions} collapsed={collapsed} onPick={() => onClose?.()} />
      )}

      <div className={cn('flex-1 space-y-4 overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}>
        {q && !shown.some((s) => s.items.length) && (
          <p className="px-3 text-caption text-neutral-500">No page called “{query.trim()}”.</p>
        )}
        {shown.map((section, i) => {
          const visible = section.items;
          if (visible.length === 0) return null;
          return (
            <div key={section.label ?? i}>
              {section.label && !collapsed && (
                <div className="px-3 pb-1.5 text-micro font-medium uppercase tracking-wider text-neutral-500">
                  {section.label}
                </div>
              )}
              {/* Collapsed, the label is gone but the grouping must not be —
                  a hairline keeps the sections readable as sections. */}
              {section.label && collapsed && i > 0 && (
                <div className="mx-2 mb-2 border-t border-neutral-200" />
              )}
              <ul className="space-y-0.5">
                {/* Already filtered by `mergedSections`, which has to know what
                    is visible to decide whether a section renders at all. */}
                {visible.map((item) => (
                  <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={isParent(item.to)}
                        title={collapsed ? item.label : undefined}
                        onClick={() => setQuery('')}
                        className={({ isActive }) => cn(
                          // 40px in the phone's drawer: a 36px row is a near miss for a thumb.
                          'relative flex items-center gap-2.5 rounded-btn text-body transition-colors duration-hover max-md:min-h-10',
                          collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
                          isActive
                            ? 'bg-primary-50 font-semibold text-primary-700'
                            : 'font-normal text-neutral-700 hover:bg-neutral-100',
                        )}
                      >
                        {({ isActive }) => (
                          <>
                            {/* The logo's magenta, as a thin mark only: the
                                one accent the consoles allow (option A). */}
                            {isActive && (
                              <span aria-hidden className={cn('absolute top-1.5 bottom-1.5 w-[3px] rounded-full bg-accent-500', collapsed ? '-left-2' : '-left-3')} />
                            )}
                            <item.icon
                              className={cn('h-5 w-5 shrink-0',
                                isActive ? 'text-primary-600' : 'text-neutral-500')}
                              strokeWidth={2}
                            />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                            {collapsed && <span className="sr-only">{item.label}</span>}
                          </>
                        )}
                      </NavLink>
                      {/* The pages inside this one, while the reader is in it. */}
                      {!collapsed && !q && (item.children?.length ?? 0) > 1
                        && (pathname === item.to || pathname.startsWith(`${item.to}/`)) && (
                        <ul className="mb-1 ml-[1.375rem] mt-0.5 space-y-0.5 border-l border-neutral-300 pl-3">
                          {item.children!.map((ch) => (
                            <li key={ch.to}>
                              <NavLink
                                to={ch.to}
                                end={ch.end}
                                className={({ isActive }) => cn(
                                  'relative flex items-center rounded-btn px-2.5 py-1.5 text-small transition-colors duration-hover max-md:min-h-10',
                                  isActive ? 'font-medium text-primary-700' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                                )}
                              >
                                {({ isActive }) => (
                                  <>
                                    <span aria-hidden className={cn('absolute -left-[17px] h-2 w-2 rounded-full border-2 border-white',
                                      isActive ? 'bg-primary-600' : 'bg-neutral-300')} />
                                    {ch.label}
                                  </>
                                )}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* The theme control, for the drawer.
          The header carries it from `sm` up, where there is room; on a phone
          the drawer is the only place a fourth header control could go without
          pushing the console name off the screen. `onClose` is present only in
          the drawer, which is exactly the case that needs it. */}
      {onClose && (
        <div className="border-t border-neutral-200 px-3 py-3">
          <div className="px-3 pb-1.5 text-micro uppercase tracking-wider text-neutral-500">
            Appearance
          </div>
          <ThemeToggle className="w-full justify-between" />
        </div>
      )}
    </nav>
  );
}

/**
 * The page heading row.
 *
 * A primitive so that the title, the sentence under it and the actions land in
 * the same place on all twenty-nine screens. When each page positions its own,
 * the reader's eye has to re-find the title on every navigation.
 */
export function PageHeader({ title, hint, actions, className }: {
  title: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-5 flex flex-wrap items-start justify-between gap-3', className)}>
      {/* A basis, so on a phone the title takes the line and the actions
          wrap under it, instead of both squeezing into half a screen each. */}
      <div className="min-w-0 flex-[1_1_18rem]">
        {/* What the page is for sits behind ⓘ, so a screen opens on its
            data rather than on a paragraph (owner's review, 14 Oct 2026). */}
        <h1 className="flex items-center gap-1.5 text-h2 text-neutral-900">
          <span className="min-w-0">{title}</span>
          {hint && <InfoTip label={`About ${typeof title === 'string' ? title : 'this page'}`}>{hint}</InfoTip>}
        </h1>
      </div>
      {/* Shrinks on a phone: a full-width control among the actions (a venue
          picker) would otherwise size this box to its content and push the
          page sideways. From `sm` up it keeps its natural width. */}
      {actions && <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">{actions}</div>}
    </div>
  );
}
